import {
  Step,
  Stack,
  Dialog,
  Stepper,
  StepLabel,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@material-ui/core";
import { Close, OpenInFull, CloseFullscreen } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SetStateAction } from "react";
import { useBulkMessageDelaySettings } from "hooks/useBulkMessageDelaySettings";
import useFirestore from "hooks/useFirestore";
import { getFilesData } from "utils/fileValidation";
import { FileOrFilePreview, FilePreview } from "../../../../../@types/listings/fileData";
import useContactWrites from "hooks/contacts/useContactWrites";
import useContactsLookup from "hooks/contacts/useContactsLookup";
import { contactIdForLookup } from "hooks/contacts/contactLookupKey";
import { useLocalStorage } from "usehooks-ts";
import { getBulkMessagePreviewId } from "./BulkMessageDialogContactPreview";
import {
  BulkMessageDialogSendMessageStep,
  BulkMessageDialogSendMessageStepActions
} from "./BulkMessageDialogSendMessageStep";
import {
  BulkMessageDialogWriteMessageStep,
  BulkMessageDialogWriteMessageStepActions
} from "./BulkMessageDialogWriteMessageStep";
import useExtensionWithFirebase from "hooks/useExtensionWithFirebase";
import {
  describePresence,
  isSafeToRetry,
  type ExtensionOperationResult
} from "logic/extensionWithFirebase";
import useWebotServer from "hooks/useWebotServer";
import { checkBlockedNumbers } from "utils/whatsapp/blockedGate";
import { useWebotStatus } from "hooks/useWebotStatus";
import { ExtensionIsNotActive } from "components/_dashboard/importContacts/ExtensionIsNotActive";
import ChromeExtensionStatusSignal from "components/__haim/Common/ChromeExtensionStatusSignal";
import moment from "moment";
import useAuth from "hooks/useAuth";
import { createFavoriteIfNotExistsAndAddListingToFavorites } from "utils/Haim/firestoreUtils";
import { PATH_PUBLIC } from "routes/paths";
import {
  BULK_MESSAGE_STEPS,
  initialActiveTemplates,
  ALLOWED_FILE_MIME_TYPES,
  setInitialSelectedMessageStatus,
  setInitialPlaceholderTemplateForm,
  getSkipContactReasons,
  hasRecentBulkMessageInLog,
  MAX_CONSECUTIVE_SEND_FAILURES,
  DEDUPE_LOOKBACK_SECONDS,
  type ActiveFieldTemplate,
  type DelayStatus,
  type BulkMessageDelaySettings,
  type BulkMessageRecipient
} from "utils/contacts/bulkMessage/new";
import { randomNumber } from "utils/Haim/utils";
import { workerSleep } from "utils/Haim/workerTimer";
import {
  isDocumentFormat,
  processMessageTemplate,
  uploadFileToFirebaseStorage,
  getFormattedTemplateStringFromFieldKey,
  fetchMinisiteLinksForContacts
} from "utils/contacts/bulkMessage/helper";
import type { CustomContactTemplateValues } from "utils/contacts/bulkMessage/helper";
import { useModal } from "hooks/useModal";
import { BulkMessageDialogConfirmExit } from "./BulkMessageDialogConfirmExit";
import { MAX_MESSAGES_IN_FREE_TRIAL } from "logic/Consts";
import { BulkMessageDialogTrialEndedBanner } from "./BulkMessageDialogTrialEndedBanner";
import type {
  SelectedMessageStatus,
  BulkMessageTemplateType,
  PlaceholderTemplateForm,
  BulkMessageContactTemplateKey,
  CustomTemplateField,
  PerContactData
} from "utils/contacts/bulkMessage/new";
import type { DialogProps } from "@material-ui/core";
import type { AlgoliaHit } from "instantsearch.js";
import type { ProcessedListingData, BulkMessageSentMessage } from "../../../../../@types/sharedSchema";

/**
 * How one contact's send ended. `retry`: nothing was sent (no WhatsApp tab could take it) — wait and
 * queue it again. `unknown`: WhatsApp took it but never confirmed — never queued again on its own.
 */
type SendResult = { kind: "done" | "failed" | "retry" | "unknown" };

type NewBulkMessageDialogProps = Pick<DialogProps, "open"> & {
  listing?: AlgoliaHit<ProcessedListingData>;
  selected: string[];
  showMinisiteLinkTemplate?: boolean;
  /** Caller-defined template fields (shown in template manager as "custom" group) */
  customTemplateFields?: CustomTemplateField[];
  /** Per-contact custom data: contactId → { fieldId → value } */
  perContactData?: PerContactData;
  /** Hide default contact and listing template groups, show only custom fields */
  hideDefaultTemplates?: boolean;
  /**
   * The recipients, when the HOST already holds them: an audience's members, the matches
   * of a listing, the old contacts page's own rows.
   *
   * A host that passes nothing gets them looked up — `selected` is resolved through
   * `useContactsLookup`, one batched request for exactly the ids being messaged. (It used
   * to fall back to `useLegacyContactsBook().allContacts`, the whole downloaded book, which is empty on
   * every host that has not downloaded it: recipients then had no name for a template and
   * no opt-out or recency for the skip rules.)
   */
  contacts?: BulkMessageRecipient[];
  /**
   * Persist the outcome of one send. Defaults to recording it server-side against the
   * caller's OWN book (`POST /api/v1/contacts/record-bulk-message`), which is right for
   * every host whose audience is the caller's own contacts.
   *
   * Pass this only when the audience can include contacts held by a COLLEAGUE (an agency
   * audience, the agency's matches): the default addresses the caller's book, so such a
   * recipient would read as "not a contact" and go unstamped.
   */
  recordSend?: (
    contactId: string,
    outcome: "sent" | "number_missing",
    sentMessage?: BulkMessageSentMessage
  ) => Promise<void>;
  onClose: () => void;
};

export type UploadedFile = {
  src: string;
  name: string;
};

/** Stable identity for "nothing to look up" — the host supplied the recipients itself. */
const NO_RECIPIENT_IDS: string[] = [];

export function NewBulkMessageDialog({
  open,
  listing,
  selected,
  showMinisiteLinkTemplate,
  customTemplateFields,
  perContactData,
  hideDefaultTemplates,
  contacts,
  recordSend,
  onClose
}: NewBulkMessageDialogProps): JSX.Element {
  const { recordBulkMessage } = useContactWrites();

  /**
   * Who `selected` are. The host's own list when it passed one; otherwise looked up.
   *
   * A looked-up recipient is re-keyed to the id the HOST selected, which may be a phone in
   * another spelling (the rental-to-sale board selects by the number on the ad): that id is
   * what every step below matches on and what the message is sent to.
   *
   * `audienceReady` is what the dialog WAITS for. The skip rules — opted out, in the spam
   * list, messaged in the last 30 days — are read off these recipients, so the per-recipient
   * statuses are built only once the lookups have come back, and "next" stays disabled until
   * then.
   *
   * A lookup that FAILED ends the wait too (`isResolving`, not `allAnswered`): the recipient
   * is then treated as one nothing is known about — its template fields take their
   * placeholders — which is how this dialog has always treated a number that is not a
   * contact. Nobody is messaged on the strength of that: the blocked-numbers gate before
   * every send is a separate server check and fails closed.
   */
  const { lookup, isResolving } = useContactsLookup(contacts ? NO_RECIPIENT_IDS : selected, {
    enabled: open && !contacts
  });
  const audience = useMemo<BulkMessageRecipient[]>(() => {
    if (contacts) return contacts;
    const found: BulkMessageRecipient[] = [];
    for (const id of selected) {
      const summary = lookup(id);
      if (summary) found.push({ ...summary, id });
    }
    return found;
  }, [contacts, selected, lookup]);
  const audienceReady = Boolean(contacts) || !isResolving;

  /**
   * Persist one send outcome onto the recipient's contact.
   *
   * The default records it server-side against the caller's OWN book
   * (`POST /contacts/record-bulk-message`). That endpoint has no create branch — a
   * recipient who is not a contact is answered `notAContact`, and is simply not stamped — so the default is
   * safe on a host that has never loaded the book. (It used to be `useLegacyContactWrites().updateContact`
   * behind an "is this id in `allContacts`" guard: unusable on such a host, and a phantom
   * contact the moment the guard was wrong.)
   *
   * Best-effort either way: by the time this runs the message has been sent, so a failure
   * to record it must not repaint the row as a failed send.
   */
  const persistOutcome = useCallback(
    async (contactId: string, outcome: "sent" | "number_missing", sentMessage?: BulkMessageSentMessage) => {
      if (recordSend) {
        await recordSend(contactId, outcome, sentMessage);
        return;
      }
      try {
        await recordBulkMessage({
          // A host may select by raw phone (the rental-to-sale board does); the contact,
          // if there is one, is filed under the canonical form.
          contactId: contactIdForLookup(contactId) ?? contactId,
          outcome,
          ...(outcome === "sent"
            ? { sentMessage: { message: sentMessage?.message, mediaLink: sentMessage?.mediaLink } }
            : {})
        });
      } catch (error) {
        console.error("Recording the bulk-send outcome failed (the message itself was sent)", error);
      }
    },
    [recordSend, recordBulkMessage]
  );

  const [expanded, setExpanded] = useLocalStorage("bulk-message-dialog-expanded", false);
  const [loaded, setLoaded] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [uploadedFile, setUploadedFile] = useState<UploadedFile>({ src: "", name: "" });

  const [filterInput, setFilterInput] = useState("");
  const [messageInput, setMessageInput] = useLocalStorage("last-bulk-message-input", "");

  const [filesPreview, setFilesPreview] = useState<FilePreview[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<FileOrFilePreview[]>([]);

  const [autoScroll, setAutoScroll] = useState(true);
  const [agreeToUserAgreement, setAgreeToUserAgreement] = useState(false);
  const [filterRecentContactsBulkMessages, setFilterRecentContactsBulkMessages] = useState(false);

  const [placeholderTemplatesForm, setPlaceholderTemplatesForm] = useState<PlaceholderTemplateForm>(
    setInitialPlaceholderTemplateForm
  );

  const [selectedMessageStatus, setSelectedMessageStatus] = useState<SelectedMessageStatus>(() =>
    setInitialSelectedMessageStatus(selected, audience, filterRecentContactsBulkMessages)
  );

  const [sendingSingleMessage, setSendingSingleMessage] = useState(false);
  const [sendingMultipleMessage, setSendingMultipleMessage] = useState(false);

  const [sendingProgressCount, setSendingProgressCount] = useState(0);
  const [expandAllPreviews, setExpandAllPreviews] = useState<boolean | undefined>(undefined);

  const [hasSentMessage, setHasSentMessage] = useState(false);

  const [activeTemplates, setActiveTemplates] = useState<ActiveFieldTemplate>(() =>
    buildActiveTemplates()
  );

  function buildActiveTemplates(): ActiveFieldTemplate {
    let templates = hideDefaultTemplates ? ({} as any) : { ...initialActiveTemplates };

    if (!hideDefaultTemplates && showMinisiteLinkTemplate) {
      templates.minisiteLink = { ...initialActiveTemplates.minisiteLink, active: true };
    }

    if (customTemplateFields) {
      for (const field of customTemplateFields) {
        templates[field.id] = {
          id: field.id,
          type: "custom" as any,
          active: field.active ?? true,
          placeholder: ""
        };
      }
    }

    return templates;
  }

  /**
   * Account-level, not `useLocalStorage`.
   *
   * Pacing is a property of how cautious this ACCOUNT wants to be with its WhatsApp
   * number, so it follows the realtor between their laptop and their phone and is
   * editable at /dashboard/settings/bulkMessaging. `useBulkMessageDelaySettings` promotes
   * any value the old `bulk-message-delay-settings` key still holds, so a realtor who had
   * tuned this keeps their tuning.
   *
   * `setDelaySettings` keeps the `Dispatch<SetStateAction<…>>` shape the step component
   * and `BulkMessageDialogDelaySettings` already take — the form is shared with the
   * settings section and neither host changes it.
   */
  const { delaySettings: savedDelaySettings, saveDelaySettings } = useBulkMessageDelaySettings();
  const setDelaySettings = useCallback(
    (update: SetStateAction<BulkMessageDelaySettings>) => {
      const next = typeof update === "function" ? update(savedDelaySettings) : update;
      saveDelaySettings(next).catch((error) => console.error(error));
    },
    [savedDelaySettings, saveDelaySettings]
  );
  const delaySettings = savedDelaySettings;

  const [delayStatus, setDelayStatus] = useState<DelayStatus>({
    type: "idle",
    remainingSeconds: 0
  });

  const [minisiteLinks, setMinisiteLinks] = useState<Record<string, string>>({});
  const [loadingMinisiteLinks, setLoadingMinisiteLinks] = useState(false);

  const {
    open: warningModalOpen,
    openModal: openWarningModal,
    closeModal: closeWarningModal
  } = useModal();

  const { t: translate } = useTranslation();

  const { user } = useAuth();

  const { userSettings, messagesSentCount, saveSentMessage } = useFirestore();

  const extensionService = useExtensionWithFirebase();

  const { whatsappBlacklistCheck } = useWebotServer();

  const { appStatus, dataReady } = useWebotStatus();

  const isSendingRef = useRef(sendingMultipleMessage);
  /**
   * Per contact, across waits and retries: how many parts of a two-part send went out, and when an attempt
   * WhatsApp never confirmed was made (a retry then has the extension look in the chat first).
   */
  const sendProgressRef = useRef<
    Record<string, { partsSent: number; mayHaveBeenSentAt?: number; neverResendAutomatically?: boolean }>
  >({});
  const mutableScroll = useRef(false);
  const isAutoScrollRef = useRef(autoScroll);

  const delaySettingsRef = useRef(delaySettings);

  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const messagePreviewListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!appStatus) return;
    setLoaded(true);
  }, [appStatus]);

  useEffect(() => {
    isAutoScrollRef.current = autoScroll;
  }, [autoScroll]);

  useEffect(() => {
    isSendingRef.current = sendingMultipleMessage;
  }, [sendingMultipleMessage]);

  useEffect(() => {
    delaySettingsRef.current = delaySettings;
  }, [delaySettings]);

  useEffect(() => {
    if (open) return;

    const resetDialogState = (): void => {
      sendProgressRef.current = {};
      setActiveStep(0);
      setFilesPreview([]);
      setSelectedFiles([]);
      setHasSentMessage(false);
      setMinisiteLinks({});
      setActiveTemplates(buildActiveTemplates());
      setPlaceholderTemplatesForm(setInitialPlaceholderTemplateForm());
    };

    resetDialogState();
    resetSendMessageState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected]);

  useEffect(() => {
    if (!open) return;
    if (activeStep === 1) return;

    resetSendMessageState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected, activeStep]);

  useEffect(() => {
    if (!open || !audienceReady) return;

    // Keyed on `audienceReady`, deliberately NOT on `audience`: that array is rebuilt every
    // time the lookup store changes — including when a send is recorded — and rebuilding the
    // statuses then would reset a run in progress.
    setSelectedMessageStatus(
      setInitialSelectedMessageStatus(selected, audience, filterRecentContactsBulkMessages)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, audienceReady, filterRecentContactsBulkMessages]);

  const countdownDelay = async (
    totalMs: number,
    type: "delaying" | "cooldown"
  ): Promise<void> => {
    const intervalMs = 1000;
    // Compute remaining from an absolute end time so the countdown stays
    // accurate even if a tick is delayed (e.g. tab throttling / event-loop lag).
    const endTime = Date.now() + totalMs;

    let remaining = totalMs;
    while (remaining > 0 && isSendingRef.current) {
      setDelayStatus({ type, remainingSeconds: Math.ceil(remaining / 1000) });
      // workerSleep runs the timer in a Web Worker, which browsers do NOT
      // throttle in background tabs — so sending keeps going when the user
      // switches away from the dashboard tab.
      const { promise } = workerSleep(Math.min(intervalMs, remaining));
      await promise;
      remaining = endTime - Date.now();
    }

    setDelayStatus({ type: "idle", remainingSeconds: 0 });
  };

  useEffect(() => {
    if (!open) return;

    if (!sendingMultipleMessage) return;

    // A message an extension older than 1.0.93 may still send is left to the realtor's single-row retry.
    const isAlreadyResolved = (contactId: string): boolean =>
      ["success", "skipped"].includes(selectedMessageStatus[contactId].status) ||
      !!sendProgressRef.current[contactId]?.neverResendAutomatically;

    const resolveSkipReason = async (contactId: string): Promise<string | null> => {
      const contact =
        selectedMessageStatus[contactId]?.data ?? audience.find((c) => c.id === contactId);

      const syncReasons = getSkipContactReasons(contact, filterRecentContactsBulkMessages);
      if (syncReasons.length) return syncReasons[0];

      if (filterRecentContactsBulkMessages) {
        const { hasRecent, failed } = await hasRecentBulkMessageInLog(user.email, contactId);
        if (hasRecent) {
          return failed ? "sentMessagesLogQueryFailed" : "numberHasReceivedMessageInLast30Days";
        }
      }

      return null;
    };

    const markSkipped = (contactId: string, tooltip: string): void => {
      setSelectedMessageStatus((prev) => ({
        ...prev,
        [contactId]: { ...prev[contactId], status: "skipped", customTooltip: tooltip }
      }));
    };

    const markError = (contactId: string, tooltip: string): void => {
      setSelectedMessageStatus((prev) => ({
        ...prev,
        [contactId]: { ...prev[contactId], status: "error", customTooltip: tooltip }
      }));
    };

    /**
     * FAIL-CLOSED blocked-numbers gate (server-side list: personal + org blocks, incl. numbers
     * that are not contacts). A throw means the check could not be completed — the caller must
     * then send NOTHING from `ids`. `getSkipContactReasons` below stays as the synchronous local
     * layer; this is the authoritative one.
     */
    const gateBlocked = (ids: string[]) => checkBlockedNumbers(whatsappBlacklistCheck, ids);

    const BLOCK_CHECK_FAILED_TOOLTIP = "בדיקת המספרים החסומים נכשלה — ההודעה לא נשלחה";

    const handleSend = async (): Promise<void> => {
      setSendingProgressCount(0);
      setDelayStatus({ type: "idle", remainingSeconds: 0 });
      let actualSentCount = 0;
      let consecutiveFailures = 0;

      const unresolved = selected.filter((contactId) => !isAlreadyResolved(contactId));
      let blockedByPhone: Awaited<ReturnType<typeof gateBlocked>>;
      try {
        blockedByPhone = await gateBlocked(unresolved);
      } catch (err) {
        console.error("Blocked-numbers check failed — aborting the bulk send", err);
        for (const contactId of unresolved) {
          markError(contactId, BLOCK_CHECK_FAILED_TOOLTIP);
        }
        setSendingMultipleMessage(false);
        setHasSentMessage(true);
        return;
      }

      for (let i = 0; i < selected.length; i++) {
        const contactId = selected[i];

        if (!isSendingRef.current) return;

        if (isAlreadyResolved(contactId)) {
          setSendingProgressCount((prev) => prev + 1);
          continue;
        }

        const blockedRow = blockedByPhone.get(contactId);
        if (blockedRow) {
          markSkipped(contactId, `המספר חסום לדיוור — ${blockedRow.hebrew}`);
          setSendingProgressCount((prev) => prev + 1);
          continue;
        }

        const skipReason = await resolveSkipReason(contactId);
        if (skipReason) {
          markSkipped(contactId, skipReason);
          setSendingProgressCount((prev) => prev + 1);
          continue;
        }

        if (isAutoScrollRef.current) {
          mutableScroll.current = true;

          const currentPreviewMessageElement = document.getElementById(
            getBulkMessagePreviewId(contactId)
          );

          currentPreviewMessageElement?.scrollIntoView({
            block: "center",
            behavior: "smooth"
          });
        }

        const result = await sendWhenWhatsAppIsReady(contactId);
        if (!result) return; // the realtor stopped the run while it waited for WhatsApp

        // Sending that keeps failing in WhatsApp itself (not "this number is not on WhatsApp"), or keeps
        // going unanswered, stops the run instead of going through the whole list.
        consecutiveFailures =
          result.kind === "failed" || result.kind === "unknown" ? consecutiveFailures + 1 : 0;
        if (consecutiveFailures >= MAX_CONSECUTIVE_SEND_FAILURES) {
          setDelayStatus({
            type: "stopped",
            remainingSeconds: 0,
            message: `השליחה נעצרה: ${MAX_CONSECUTIVE_SEND_FAILURES} הודעות ברצף נכשלו ב-WhatsApp. רעננו את לשונית WhatsApp Web והמשיכו את השליחה`
          });
          setSendingMultipleMessage(false);
          setHasSentMessage(true);
          return;
        }

        setSendingProgressCount((prev) => prev + 1);
        actualSentCount++;

        const hasMoreContacts = i < selected.length - 1;

        if (hasMoreContacts && isSendingRef.current) {
          const settings = delaySettingsRef.current;

          const isCooldownPoint =
            settings.cooldownEnabled &&
            actualSentCount > 0 &&
            actualSentCount % settings.cooldownAfterCount === 0;

          if (isCooldownPoint) {
            await countdownDelay(settings.cooldownDurationSeconds * 1000, "cooldown");
            // A long run can outlive an edit to the blocked list — re-check the remaining tail
            // at every cooldown so a number blocked mid-run stops receiving the rest of it.
            const tail = selected.slice(i + 1).filter((id) => !isAlreadyResolved(id));
            if (tail.length > 0 && isSendingRef.current) {
              try {
                const refreshed = await gateBlocked(tail);
                refreshed.forEach((row, phone) => blockedByPhone.set(phone, row));
              } catch (err) {
                console.error("Blocked-numbers re-check failed — aborting the rest of the run", err);
                for (const id of tail) {
                  markError(id, BLOCK_CHECK_FAILED_TOOLTIP);
                }
                setSendingMultipleMessage(false);
                setHasSentMessage(true);
                return;
              }
            }
          } else {
            const delayMs = randomNumber(
              settings.delayMinSeconds * 1000,
              settings.delayMaxSeconds * 1000
            );

            if (delayMs > 0) {
              await countdownDelay(delayMs, "delaying");
            }
          }
        }
      }

      if (isSendingRef.current) {
        setSendingMultipleMessage(false);
      }

      setHasSentMessage(true);
    };

    void handleSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sendingMultipleMessage]);

  useEffect(() => {
    const skipScrollCheck = !autoScroll || !sendingMultipleMessage;

    if (skipScrollCheck) return;

    const scrollableElement = messagePreviewListRef.current;

    if (!scrollableElement) return;

    const handleManualScroll = () => {
      if (mutableScroll.current) {
        mutableScroll.current = false;
        return;
      }

      setAutoScroll(false);
    };

    scrollableElement.addEventListener("wheel", handleManualScroll);
    scrollableElement.addEventListener("touchmove", handleManualScroll);

    return () => {
      scrollableElement.removeEventListener("wheel", handleManualScroll);
      scrollableElement.removeEventListener("touchmove", handleManualScroll);
    };
  }, [open, activeStep, autoScroll, sendingMultipleMessage]);

  const resetSendMessageState = (): void => {
    setAutoScroll(true);
    setSendingProgressCount(0);
    setAgreeToUserAgreement(false);
    setSendingMultipleMessage(false);
    setDelayStatus({ type: "idle", remainingSeconds: 0 });
    setSelectedMessageStatus(
      setInitialSelectedMessageStatus(selected, audience, filterRecentContactsBulkMessages)
    );
  };

  const bulkMessageType: BulkMessageTemplateType = listing ? "listing" : "contact";
  const isPaidTier = userSettings?.advancedFeatures?.includes("bulkMessaging");

  /**
   * Sends one contact's message and says how it ended. `retry` means nothing was sent (no WhatsApp tab
   * took it, or WhatsApp was not ready): the caller waits for WhatsApp and queues it again. A message
   * WhatsApp took but never confirmed is `unknown`: never queued again on its own, and a manual retry
   * makes the extension look in the chat first.
   */
  const handleSendMessage = async (contactId: string): Promise<SendResult> => {
    setSelectedMessageStatus((prev) => ({
      ...prev,
      [contactId]: {
        ...prev[contactId],
        status: "loading"
      }
    }));

    const contactSelectedMessageStatus = selectedMessageStatus[contactId];

    const contact = contactSelectedMessageStatus?.data ?? audience.find((c) => c.id === contactId);

    const parsedMessageInput = contactSelectedMessageStatus.customMessage || messageInput;

    try {
      let { src: fileSrc, name: fileName } = await handleFileUploadToFirebase();

      let generatedListingLink: string | null = null;

      const listingLinkExistsInMessage = parsedMessageInput.includes(
        getFormattedTemplateStringFromFieldKey("listingLink")
      );

      const shouldUpsertListingLink = bulkMessageType === "listing" && listingLinkExistsInMessage;

      if (shouldUpsertListingLink) {
        const favoriteListingId = await createFavoriteIfNotExistsAndAddListingToFavorites(
          user.email,
          contactId,
          listing.objectID
        );

        generatedListingLink = `${window.location.origin}/${PATH_PUBLIC.myListings}/${favoriteListingId}/${listing.objectID}`;
      }

      const customContactValues: CustomContactTemplateValues = {};

      if (minisiteLinkUsedInMessage) {
        customContactValues.minisiteLink = minisiteLinks[contactId] ?? "";
      }

      let processedMessage = processMessageTemplate(
        parsedMessageInput,
        contact,
        listing,
        placeholderTemplatesForm,
        generatedListingLink,
        customContactValues,
        perContactData?.[contactId]
      );

      if (!isPaidTier) {
        fileName = `WEBOT-TRIAL-${fileName}`;

        processedMessage += "\n";
        processedMessage += "\n";
        processedMessage += "---------";
        processedMessage += "\n";
        processedMessage += translate("contactsPage.bulkMessage.freeTierTemplate");
        processedMessage += "\n";
        processedMessage += "https://webot.co.il";
      }

      const sendMessageWithDocumentFormat = fileSrc && isDocumentFormat(fileName);

      // A document goes as two messages (the text, then the file). A part that went out is never sent
      // again when the other one has to wait for WhatsApp.
      const parts: Array<[string, string, string]> = sendMessageWithDocumentFormat
        ? [
            [processedMessage, "", ""],
            ["", fileSrc, fileName]
          ]
        : [[processedMessage, fileSrc, fileName]];
      if (!sendProgressRef.current[contactId]) sendProgressRef.current[contactId] = { partsSent: 0 };
      const progress = sendProgressRef.current[contactId];

      let response: ExtensionOperationResult = { outcome: "success" };
      for (let part = progress.partsSent; part < parts.length; part++) {
        const [text, link, name] = parts[part];
        const attemptAt = Math.floor(Date.now() / 1000) - DEDUPE_LOOKBACK_SECONDS;
        response = await extensionService.sendWhatsAppMessage(
          contactId,
          text,
          link,
          name,
          progress.mayHaveBeenSentAt
        );
        if (response.outcome === "unknown" && progress.mayHaveBeenSentAt === undefined) {
          progress.mayHaveBeenSentAt = attemptAt;
        }
        if (response.outcome !== "success") break;
        progress.partsSent = part + 1;
        progress.mayHaveBeenSentAt = undefined;
      }

      if (isSafeToRetry(response.outcome)) {
        setSelectedMessageStatus((prev) => ({
          ...prev,
          [contactId]: { ...prev[contactId], status: "loading", customTooltip: "ממתין ל-WhatsApp Web" }
        }));
        return { kind: "retry" };
      }

      if (response.outcome === "unknown") {
        if (response.neverResendAutomatically) progress.neverResendAutomatically = true;
        setSelectedMessageStatus((prev) => ({
          ...prev,
          [contactId]: {
            ...prev[contactId],
            status: "warning",
            customTooltip:
              response.realtorMessage ??
              "WhatsApp קיבל את ההודעה אך לא אישר שנשלחה. ייתכן שנשלחה: בדקו בצ'אט. שליחה חוזרת תבדוק קודם בצ'אט ולא תשלח פעמיים"
          }
        }));
        return { kind: "unknown" };
      }

      if (response.operationStatus === "success") {
        delete sendProgressRef.current[contactId];
        const sentMessage: BulkMessageSentMessage = {
          sentTo: contactId,
          message: processedMessage,
          sentTime: moment.utc().toISOString(),
          listingId: listing ? listing.originalListingId : null,
          mediaLink: fileSrc,
          customFileName: fileName
        };

        await saveSentMessage(sentMessage);

        await persistOutcome(contactId, "sent", sentMessage);

        setSelectedMessageStatus((prev) => ({
          ...prev,
          [contactId]: {
            ...prev[contactId],
            status: "success"
          }
        }));
        return { kind: "done" };
      } else if (
        response.operationErrorCode === "NUMBER_NOT_ON_WHATSAPP" ||
        response.operationError?.includes("Can't find Chat")
      ) {
        delete sendProgressRef.current[contactId];
        await persistOutcome(contactId, "number_missing");

        setSelectedMessageStatus((prev) => ({
          ...prev,
          [contactId]: {
            ...prev[contactId],
            status: "warning",
            customTooltip: "numberDoesNotExistOnWhatsApp"
          }
        }));
        return { kind: "done" };
      } else {
        setSelectedMessageStatus((prev) => ({
          ...prev,
          [contactId]: {
            ...prev[contactId],
            status: "error",
            customTooltip: response.operationError || "unknownError"
          }
        }));
        delete sendProgressRef.current[contactId];
        return { kind: "failed" };
      }
    } catch (err) {
      console.error("Error sending bulk message to contact", err);

      setSelectedMessageStatus((prev) => ({
        ...prev,
        [contactId]: {
          ...prev[contactId],
          status: "error",
          customTooltip: err instanceof Error ? err.message : "unknownError"
        }
      }));
      delete sendProgressRef.current[contactId];
      return { kind: "failed" };
    }
  };

  /**
   * Sends one contact's message, waiting for WhatsApp Web whenever no tab can take it (closed, asleep,
   * logged out, open in another window): the run pauses and says why, and never skips the message.
   * Resolves false when the realtor stopped the run while it waited.
   */
  const sendWhenWhatsAppIsReady = async (contactId: string): Promise<SendResult | null> => {
    let missedAt: number | undefined;
    for (;;) {
      const ready = await extensionService.waitUntilReady({
        since: missedAt,
        isCancelled: () => !isSendingRef.current,
        onWaiting: (presence) =>
          setDelayStatus({ type: "waiting", remainingSeconds: 0, message: describePresence(presence) })
      });
      setDelayStatus({ type: "idle", remainingSeconds: 0 });
      if (!ready) return null;

      const result = await handleSendMessage(contactId);
      if (result.kind !== "retry") return result;
      missedAt = Date.now();
    }
  };

  const handleRetrySendingSingleBulkMessage = (contactId: string) => async (): Promise<void> => {
    setSendingSingleMessage(true);

    try {
      // Same fail-closed gate as the bulk loop — a per-row retry must not slip past it.
      const blocked = await checkBlockedNumbers(whatsappBlacklistCheck, [contactId]);
      const blockedRow = blocked.get(contactId);
      if (blockedRow) {
        setSelectedMessageStatus((prev) => ({
          ...prev,
          [contactId]: {
            ...prev[contactId],
            status: "skipped",
            customTooltip: `המספר חסום לדיוור — ${blockedRow.hebrew}`
          }
        }));
        return;
      }
      const result = await handleSendMessage(contactId);
      if (result.kind === "retry") {
        // A single retry does not wait: say why WhatsApp could not take it.
        const presence = await extensionService.getPresence().catch(() => null);
        setSelectedMessageStatus((prev) => ({
          ...prev,
          [contactId]: { ...prev[contactId], status: "error", customTooltip: `ההודעה לא נשלחה. ${describePresence(presence)}` }
        }));
      }
    } catch (err) {
      console.error("Error retrying sending bulk message to contact", err);
      setSelectedMessageStatus((prev) => ({
        ...prev,
        [contactId]: {
          ...prev[contactId],
          status: "error",
          customTooltip: "בדיקת המספרים החסומים נכשלה — ההודעה לא נשלחה"
        }
      }));
    } finally {
      setSendingSingleMessage(false);
    }
  };

  const handleFileUpload = ({ target: { files } }: ChangeEvent<HTMLInputElement>): void => {
    const filesData = getFilesData(files, filesPreview.length, 1, 24, ALLOWED_FILE_MIME_TYPES);

    if (!filesData) {
      setSnackbarOpen(true);
      return;
    }

    const { filesPreviewData, selectedFilesData } = filesData;

    setFilesPreview([...filesPreview, ...filesPreviewData]);
    setSelectedFiles([...selectedFiles, ...selectedFilesData]);
  };

  const handleRemoveFile = (targetId: string) => (): void => {
    setFilesPreview(filesPreview.filter(({ id }) => id !== targetId));
    setSelectedFiles(selectedFiles.filter(({ id }) => id !== targetId));

    const { src } = filesPreview.find(({ id }) => id === targetId) ?? {};

    URL.revokeObjectURL(src);
  };

  const handleFileUploadToFirebase = async (): Promise<UploadedFile> => {
    if (!selectedFiles.length) {
      return {
        src: "",
        name: ""
      };
    }

    const file = selectedFiles[0] as File;
    const fileName = file.name;

    const isSameFileAsBefore = fileName === uploadedFile.name;

    if (isSameFileAsBefore) return uploadedFile;

    const uploadedFileSrc = await uploadFileToFirebaseStorage(user.email, file);

    const uploadFile: UploadedFile = {
      src: uploadedFileSrc,
      name: fileName
    };

    setUploadedFile(uploadFile);

    return uploadFile;
  };

  const handleGetRandomMessage = (): void => {
    let randomMessageType: "contact" | "listing" = "contact";

    if (listing) randomMessageType = "listing";

    const defaultMessages = Object.values(
      translate(`contactsPage.bulkMessage.defaultMessages.${randomMessageType}`, {
        returnObjects: true,
        interpolation: { prefix: "NO REPLACE", suffix: "NO REPLACE" } as any
      })
    );

    const randomIndex = Math.floor(Math.random() * defaultMessages.length);
    const randomMessage = defaultMessages[randomIndex];

    setMessageInput(randomMessage);
  };

  const handleInsertTemplate = (template: BulkMessageContactTemplateKey) => (): void => {
    const lastCursorPosition = messageInput.length;
    const currentCursorPosition = messageInputRef.current?.selectionStart || lastCursorPosition;

    const textBeforeCursor = messageInput.slice(0, currentCursorPosition).trim();
    const textAfterCursor = messageInput.slice(currentCursorPosition, lastCursorPosition).trim();

    const templateText = getFormattedTemplateStringFromFieldKey(template);

    const newValue = `${textBeforeCursor} ${templateText} ${textAfterCursor}`.trim();

    setMessageInput(newValue);

    messageInputRef.current?.focus();

    setTimeout(() => {
      let nextCursorPosition = currentCursorPosition + templateText.length + 1;

      messageInputRef.current?.setSelectionRange(nextCursorPosition, nextCursorPosition);
    }, 0);
  };

  const minisiteLinkUsedInMessage = messageInput.includes(
    getFormattedTemplateStringFromFieldKey("minisiteLink")
  );

  useEffect(() => {
    if (!open || activeStep !== 1 || !minisiteLinkUsedInMessage) return;

    setLoadingMinisiteLinks(true);

    fetchMinisiteLinksForContacts(user.email, selected).then((links) => {
      setMinisiteLinks(links);
      setLoadingMinisiteLinks(false);
    });
  }, [open, activeStep, minisiteLinkUsedInMessage, user.email, selected]);

  const handleNextStep = (): void => {
    setActiveStep(activeStep + 1);
  };

  const handleBackStep = (): void => {
    setActiveStep(activeStep - 1);
  };

  const handleSnackbarClose = (): void => setSnackbarOpen(false);

  const trialEnded = !isPaidTier && messagesSentCount >= MAX_MESSAGES_IN_FREE_TRIAL;

  const handleConfirmCloseContinue = (): void => {
    const shouldCloseWithoutWarning = trialEnded || hasSentMessage;

    if (shouldCloseWithoutWarning) {
      onClose();
      return;
    }

    openWarningModal();
  };

  const handleOnClose = (): void => {
    closeWarningModal();
    onClose();
  };

  const isWriteMessageStep = activeStep === 0;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _isSendMessageStep = activeStep === 1;

  const showTrialBanner = !isPaidTier;

  const showDialogActions = loaded && !trialEnded;

  return (
    <>
      <BulkMessageDialogConfirmExit
        open={warningModalOpen}
        onClose={handleOnClose}
        onCloseContinue={closeWarningModal}
      />
      <Dialog
        fullWidth
        open={open}
        maxWidth={expanded ? "xl" : "md"}
        onClose={handleConfirmCloseContinue}
        PaperProps={expanded ? { sx: { height: "90vh" } } : undefined}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={2} alignItems="center">
              <ChromeExtensionStatusSignal />
              <Typography variant="h6">{translate("contactsPage.bulkMessage.title")}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              <IconButton onClick={() => setExpanded(!expanded)} size="small">
                {expanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
              </IconButton>
              <IconButton onClick={handleConfirmCloseContinue}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pb: 0, scrollbarGutter: "stable" }}>
          <Stack sx={{ minHeight: 500 }} gap={2}>
            {loaded ? (
              trialEnded ? (
                <BulkMessageDialogTrialEndedBanner />
              ) : (
                <>
                  <Stepper activeStep={activeStep}>
                    {BULK_MESSAGE_STEPS.map((step) => (
                      <Step key={step}>
                        <StepLabel>{translate(`contactsPage.bulkMessage.steps.${step}`)}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                  {isWriteMessageStep ? (
                    <BulkMessageDialogWriteMessageStep
                      listing={listing}
                      selected={selected}
                      audience={audience}
                      messageInput={messageInput}
                      filesPreview={filesPreview}
                      snackbarOpen={snackbarOpen}
                      selectedFiles={selectedFiles}
                      activeTemplates={activeTemplates}
                      bulkMessageType={bulkMessageType}
                      showTrialBanner={showTrialBanner}
                      messageInputRef={messageInputRef}
                      delaySettings={delaySettings}
                      placeholderTemplatesForm={placeholderTemplatesForm}
                      setMessageInput={setMessageInput}
                      setFilesPreview={setFilesPreview}
                      setUploadedFile={setUploadedFile}
                      setSelectedFiles={setSelectedFiles}
                      setActiveTemplates={setActiveTemplates}
                      setDelaySettings={setDelaySettings}
                      setPlaceholderTemplatesForm={setPlaceholderTemplatesForm}
                      handleRemoveFile={handleRemoveFile}
                      handleFileUpload={handleFileUpload}
                      handleSnackbarClose={handleSnackbarClose}
                      handleInsertTemplate={handleInsertTemplate}
                      handleGetRandomMessage={handleGetRandomMessage}
                      customTemplateFields={customTemplateFields}
                      hideDefaultTemplates={hideDefaultTemplates}
                    />
                  ) : (
                    <BulkMessageDialogSendMessageStep
                      listing={listing}
                      selected={selected}
                      autoScroll={autoScroll}
                      filterInput={filterInput}
                      messageInput={messageInput}
                      filesPreview={filesPreview}
                      sendingMessage={sendingMultipleMessage}
                      bulkMessageType={bulkMessageType}
                      showTrialBanner={showTrialBanner}
                      delayStatus={delayStatus}
                      minisiteLinks={minisiteLinks}
                      loadingMinisiteLinks={loadingMinisiteLinks}
                      selectedMessageStatus={selectedMessageStatus}
                      messagePreviewListRef={messagePreviewListRef}
                      placeholderTemplatesForm={placeholderTemplatesForm}
                      setAutoScroll={setAutoScroll}
                      setFilterInput={setFilterInput}
                      setSelectedMessageStatus={setSelectedMessageStatus}
                      handleRetrySendingSingleBulkMessage={handleRetrySendingSingleBulkMessage}
                      perContactData={perContactData}
                      expandAllPreviews={expandAllPreviews}
                      setExpandAllPreviews={setExpandAllPreviews}
                      expanded={expanded}
                      activeTemplates={activeTemplates}
                      customTemplateFields={customTemplateFields}
                    />
                  )}
                </>
              )
            ) : !dataReady ? (
              <CircularProgress sx={{ m: "auto" }} />
            ) : (
              <ExtensionIsNotActive onBulkMessageDialog />
            )}
          </Stack>
        </DialogContent>
        {showDialogActions && (
          <DialogActions>
            {isWriteMessageStep ? (
              <BulkMessageDialogWriteMessageStepActions
                selected={selected}
                audience={audience}
                loadingAudience={!audienceReady}
                messageInput={messageInput}
                filterRecentContactsBulkMessages={filterRecentContactsBulkMessages}
                setFilterRecentContactsBulkMessages={setFilterRecentContactsBulkMessages}
                handleNextStep={handleNextStep}
              />
            ) : (
              <BulkMessageDialogSendMessageStepActions
                selected={selected}
                sendingSingleMessage={sendingSingleMessage}
                sendingMultipleMessage={sendingMultipleMessage}
                sendingProgressCount={sendingProgressCount}
                agreeToUserAgreement={agreeToUserAgreement}
                selectedMessageStatus={selectedMessageStatus}
                setSendingMultipleMessage={setSendingMultipleMessage}
                setAgreeToUserAgreement={setAgreeToUserAgreement}
                handleBackStep={handleBackStep}
              />
            )}
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
